/**
 * Facilities, Entrega 7 (ADR 0027): viagens corporativas, só a estrutura operacional de pedido e
 * acompanhamento, com as regras operacionais da Política de Reembolso de Despesas 0001/2025
 * (agência credenciada, seguro viagem obrigatório, meio de pagamento na internacional). Adiantamento,
 * prestação de contas, reembolso e limites ficam com o Financeiro. Não guarda passaporte, visto nem
 * dados de acessibilidade.
 */
import { z } from 'zod';

export const TRAVEL_TRANSPORTS = ['none', 'air', 'bus', 'car', 'other'] as const;
export const TRAVEL_SCOPES = ['domestic', 'international'] as const;
/** Viagem internacional: como as despesas serão pagas (adiantamento e cartão são do Financeiro). */
export const TRAVEL_EXPENSE_MODES = ['corporate_card', 'advance', 'none'] as const;
export const TRAVEL_STATUSES = ['requested', 'in_progress', 'completed', 'cancelled'] as const;
export type TravelStatus = (typeof TRAVEL_STATUSES)[number];

/** Próximas situações da viagem (o banco confere de novo). */
export const TRAVEL_NEXT: Record<TravelStatus, readonly TravelStatus[]> = {
  requested: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

/** Próximas situações possíveis: só a viagem aprovada pelo gestor fica em andamento. */
export function travelNextStatuses(
  status: TravelStatus,
  approvalStatus: string | null,
): readonly TravelStatus[] {
  return TRAVEL_NEXT[status].filter((s) => s !== 'in_progress' || approvalStatus === 'approved');
}

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');

export const travelRequestInput = z
  .object({
    legalEntityId: z.uuid(),
    travelerEmployeeId: z.uuid(),
    /** O gestor deste centro de custo aprova a viagem (motor de alçadas). */
    costCenterId: z.uuid(),
    reason: z.string().trim().min(3).max(1000),
    destination: z.string().trim().min(1).max(300),
    departureOn: isoDate,
    returnOn: isoDate,
    transport: z.enum(TRAVEL_TRANSPORTS).default('none'),
    lodging: z.boolean().default(false),
    scope: z.enum(TRAVEL_SCOPES).default('domestic'),
    itinerary: optional(4000),
    flight: z.boolean().default(false),
    carRental: z.boolean().default(false),
    viaAgency: z.boolean().default(true),
    agencyException: optional(1000),
    insuranceCertificate: optional(100),
    expenseMode: z.enum(TRAVEL_EXPENSE_MODES).nullable().optional(),
    supplierPartnerId: optionalId,
    estimated: z
      .object({
        amount: z
          .string()
          .trim()
          .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais'),
        currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida'),
      })
      .nullable()
      .optional(),
    notes: optional(2000),
    // Política Corporativa de Viagens (0393): os dados que o motor confere.
    categoryCode: z.string().trim().toUpperCase().max(30).nullable().default(null),
    destinationCountry: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, 'Use o código do país (2 letras)')
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .default(null),
    destinationCity: optional(120),
    distanceKm: z.number().min(0).max(40000).nullable().default(null),
    lodgingNightly: z
      .object({
        amount: z
          .string()
          .trim()
          .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números, com ponto nos decimais'),
        currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda inválida'),
      })
      .nullable()
      .default(null),
    /** Só a validade (sem o documento): a política exige um prazo depois da ida. */
    passportValidUntil: isoDate
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .default(null),
    visaRequired: z.boolean().default(false),
    restPeriodConflict: z.boolean().default(false),
    roamingRequested: z.boolean().default(false),
    upgradeRequested: z.boolean().default(false),
    checkedBaggage: z.boolean().default(false),
    hasCompanion: z.boolean().default(false),
    personalFrom: isoDate
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .default(null),
    personalUntil: isoDate
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .default(null),
    /** Justificativa da exceção à política (obrigatória quando o motor aponta exceção). */
    policyJustification: optional(2000),
  })
  .refine((v) => v.returnOn >= v.departureOn, {
    path: ['returnOn'],
    message: 'A volta não pode ser antes da ida',
  })
  .refine((v) => v.viaAgency || (v.agencyException?.length ?? 0) >= 3, {
    path: ['agencyException'],
    message: 'Explique por que não será pela agência credenciada',
  })
  .refine((v) => v.scope === 'international' || !v.expenseMode, {
    path: ['expenseMode'],
    message: 'Só na viagem internacional',
  });
export type TravelRequestInput = z.infer<typeof travelRequestInput>;

/** Meios de transporte da viagem, nos códigos da política. */
export function travelTransportCodes(t: {
  transport: string;
  flight: boolean;
  carRental: boolean;
  upgradeRequested?: boolean;
  checkedBaggage?: boolean;
}): ('air' | 'bus' | 'car_rental' | 'own_car' | 'upgrade' | 'checked_baggage')[] {
  const codes: ('air' | 'bus' | 'car_rental' | 'own_car' | 'upgrade' | 'checked_baggage')[] = [];
  if (t.flight || t.transport === 'air') codes.push('air');
  if (t.transport === 'bus') codes.push('bus');
  if (t.carRental) codes.push('car_rental');
  else if (t.transport === 'car') codes.push('own_car');
  if (t.upgradeRequested) codes.push('upgrade');
  if (t.checkedBaggage) codes.push('checked_baggage');
  return codes;
}

export const travelStatusInput = z
  .object({
    status: z.enum(TRAVEL_STATUSES),
    reason: optional(1000),
    /** Certificado do seguro viagem (obrigatório para a viagem ficar em andamento). */
    insuranceCertificate: optional(100),
  })
  .refine((v) => v.status !== 'cancelled' || (v.reason?.length ?? 0) >= 3, {
    path: ['reason'],
    message: 'Explique o cancelamento',
  });
